import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Box, Heading, Text, Card, Flex, Button } from '@radix-ui/themes';
import { IdCardIcon, PlusIcon } from '@radix-ui/react-icons';
import GraphQLTable, { Column } from '../../components/GraphQLTable/GraphQLTable';
import { EntityDisplay } from '../../components/EntityDisplay';
import {
  GetEntitiesDocument,
  GetEntitiesQuery,
} from '../../graphql/queries/GetEntities.generated';
import { FilterOperation } from '../../types';

type Entity = GetEntitiesQuery['entities']['edges'][number]['node'];

const EntityList: React.FC = () => {
  const navigate = useNavigate();

  const columns: Column<Entity>[] = [
    {
      header: 'Name',
      accessor: 'name',
      render: ({ row }) => (
        <EntityDisplay
          id={row.id}
          name={row.name}
          risks={row.risks}
        />
      ),
      sortable: true,
      sortField: 'name',
      filterable: true,
      filterField: 'name',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search by name...',
      },
    },
    {
      header: 'Location',
      accessor: 'location',
      render: ({ row }) => (
        <Text size="2" color="gray">
          {row.location || 'Unknown'}
        </Text>
      ),
      sortable: true,
      sortField: 'location',
      filterable: true,
      filterField: 'location',
      filterConfig: {
        type: 'text',
        operation: FilterOperation.ILike,
        placeholder: 'Search by location...',
      },
    },
    {
      header: 'Addresses',
      accessor: 'addresses',
      render: ({ row }) => (
        <Text size="2" color="gray">
          {row.addresses.totalCount}
        </Text>
      ),
      sortable: true,
      sortField: 'addresses.totalCount',
      filterable: true,
      filterField: 'addresses.totalCount',
      filterConfig: {
        type: 'number',
        operation: FilterOperation.Eq,
        placeholder: 'Filter by address count...',
      },
    },
    {
      header: 'Asset Value',
      render: ({ row }) => {
        // Mock asset value calculation (to be replaced with real data)
        const mockValue = (Math.random() * 10000000).toFixed(2);
        return (
          <Text size="2" color="gray">
            ${Number(mockValue).toLocaleString()}
          </Text>
        );
      },
    },
  ];

  return (
    <Box>
      <Box pb="4" px="1">
        <Flex align="center" justify="between">
          <Flex align="center" gap="3">
            <Box
              style={{
                padding: '8px',
                borderRadius: '8px',
                backgroundColor: 'var(--purple-3)',
                color: 'var(--purple-11)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <IdCardIcon width={24} height={24} />
            </Box>
            <Box>
              <Heading as="h1" size="5" weight="bold">
                Entities
              </Heading>
              <Text size="2" color="gray">
                Known entities and associated addresses
              </Text>
            </Box>
          </Flex>
          <Button onClick={() => navigate('/entities/new')}>
            <PlusIcon />
            Add Entity
          </Button>
        </Flex>
      </Box>

      <Card size="2">
        <Box
          style={{
            marginLeft: 'calc(-1 * var(--space-4))',
            marginRight: 'calc(-1 * var(--space-4))',
            marginBottom: 'calc(-1 * var(--space-4))',
          }}
        >
          <GraphQLTable<GetEntitiesQuery, typeof GetEntitiesDocument, 'entities'>
            query={GetEntitiesDocument}
            columns={columns}
            connectionPath="entities"
            count={20}
            size="2"
            removeBottomBorder={true}
            loadingMessage="Loading entities..."
            errorMessage="Error loading entities."
            noDataMessage="No entities found."
          />
        </Box>
      </Card>
    </Box>
  );
};

export default EntityList;
